<template>
    <div>
        <div class="border-b border-blue-900 px-3 py-2">
            <h2 class="font-bold leading-none">Baseline</h2>
        </div>

        <div class="my-3 px-3">
            <p class="mb-2">
                Each building's performance baseline is the average of the 2 complete years with the highest normalized net site EUI (NN site EUI) over that building's baseline period. Reports that are “pending revisions” are not included in the baseline. The baseline below is calculated based on available data but will not be final until the end of the baseline period.
            </p>
            <p><strong>Baseline Period (reporting year):</strong> {{ baselinePeriod }}</p>
            <p><strong>Baseline (NN site EUI):</strong> {{ bepsRecord.baseline_performance_display ?? 'N/A'}}</p>
        </div>

        <div class="px-3">
            <base-table
                class="report-table"
                :data="submissionList"
                :columns="columns"
                :loading="loading"
            >
                <template v-slot="{ row, index }">
                    <td :class="`${ tableRowBaseClass(index) }`">{{ row.reporting_year }}</td>
                    <td :class="`${ tableRowBaseClass(index) }`">{{ row.benchmarking_status }}</td>
                    <td :class="`${ tableRowBaseClass(index) } text-right`">
                        <span v-if="!row.is_undisclosed">{{ row.normalized_net_site_eui | number_format }}</span>
                        <span v-else>Undisclosed</span>
                    </td>
                    <td :class="`${ tableRowBaseClass(index) } text-right`">
                        <span v-if="!row.is_undisclosed">{{ row.reported_gfa | number_format }}</span>
                        <span v-else>Undisclosed</span>
                    </td>
                    <td :class="`${ tableRowBaseClass(index) } text-right`">
                        <span v-if="!row.is_undisclosed">{{ row.reported_electricity | number_format }}</span>
                        <span v-else>Undisclosed</span>
                    </td>
                    <td :class="`${ tableRowBaseClass(index) } text-right`">
                        <span v-if="!row.is_undisclosed">{{ row.reported_natural_gas | number_format }}</span>
                        <span v-else>Undisclosed</span>
                    </td>
                    <td :class="`${ tableRowBaseClass(index) } text-right`">
                        <span v-if="!row.is_undisclosed">{{ row.direct_ghg_emissions_intensity | number_format }}</span>
                        <span v-else>Undisclosed</span>
                    </td>
                    <td :class="`${ tableRowBaseClass(index) } text-right`">
                        <span v-if="!row.is_undisclosed">{{ row.direct_ghg_emissions | number_format }}</span>
                        <span v-else>Undisclosed</span>
                    </td>
                </template>
            </base-table>
        </div>
    </div>
</template>
<script>

export default {
    props: {
        building: {
            type: Object,
            default: () => {}
        },
        submissions: {
            type: Array,
            default: () => []
        },
        beps: {
            type: Object,
            default: () => {}
        }
    },
    data() {
        return {
            columns: [
                { label: 'Reporting Year', field: 'reporting_year', customHeaderClass: 'align-top border border-b-4 border-white' },
                { label: 'Benchmarking Status', field: 'benchmarking_status', customHeaderClass: 'w-32 align-top border border-b-4 border-white' },
                { label: 'NN Site EUI', field: 'normalized_net_site_eui', customHeaderClass: 'w-36 align-top border border-b-4 border-white' },
                { label: 'Reported GFA', field: 'reported_gfa', customHeaderClass: 'align-top border border-b-4 border-white' },
                { label: 'Reported Electricity (kWh)', field: 'reported_electricity', customHeaderClass: 'align-top border border-b-4 border-white' },
                { label: 'Reported Natural Gas (kBtu)', field: 'reported_natural_gas', customHeaderClass: 'align-top border border-b-4 border-white' },
                { label: 'Direct GHG Emissions Intensity (kgCO2e/ft²)', field: 'direct_ghg_emissions_intensity', customHeaderClass: 'align-top border border-b-4 border-white' },
                { label: 'Direct GHG Emissions (Metric Tons CO2e)', field: 'direct_ghg_emissions', customHeaderClass: 'w-64' }
            ],
            loading: false,
        };
    },
    computed: {
        bepsRecord() {
            return this.beps ?? {};
        },

        baselinePeriod() {
            const bepsRecord = this.bepsRecord;
            let period = 'N/A';
            if (bepsRecord) {
                if (bepsRecord.newly_constructed_building_baseline_period) {
                    period = bepsRecord?.newly_constructed_building_baseline_period || 'N/A';
                } else {
                    period = bepsRecord?.baseline_period || 'N/A';
                }
            }
            return period;
        },

        submissionList() {
            const list = [];
            for (let counter = 0; counter < this.submissions.length; counter++) {
                const submissionData = this.submissions[counter];
                const reportingDateFullYear = parseInt(submissionData.reporting_year);
                const bepsRecord = this.beps;

                const baselinePeriodStart = bepsRecord.baseline_period_start;
                const baselinePeriodEnd = bepsRecord.baseline_period_end;

                if (baselinePeriodStart && baselinePeriodEnd) {
                    if (reportingDateFullYear >= baselinePeriodStart && reportingDateFullYear <= baselinePeriodEnd) {
                        list.push({
                            reporting_year: reportingDateFullYear,
                            benchmarking_status: submissionData.status,
                            normalized_net_site_eui: submissionData.normalized_net_site_eui,
                            reported_gfa: submissionData.reported_gfa,
                            reported_electricity: parseInt(submissionData.electricity_use_grid_purchase_and_onsite_kwh),
                            reported_natural_gas: submissionData.natural_gas_use_kbtu,
                            direct_ghg_emissions_intensity: submissionData.direct_ghg_emissions_intensity,
                            direct_ghg_emissions: submissionData.direct_ghg_emissions,
                            is_undisclosed: submissionData.is_undisclosed,
                        });
                    }
                }
            }

            return list;
        }
    },

    methods: {
        tableRowBaseClass(index) {
            return index % 2 ? 'bg-blue-50' : 'bg-blue-100';
        },
    },
};

</script>
